import React, { useEffect } from 'react'
import { get } from 'lodash'
import Joi from 'joi'
import T from 'prop-types'
import { differenceInYears } from 'date-fns'
import Button from 'components/Button'
import useSession from 'hooks/useSession'
import { getBirthDate } from 'lib/birthNumber'

import getGenderText from '../../helpers/getGenderText'
import Step from '../../components/Step'

const RISKS = [
  ['age', '65 år eller eldre'],
  ['smoker', 'Røyker'],
  ['underlying_medical_condition', 'Underliggende kronisk sykdom'],
  ['healthcare_worker', 'Helsearbeider'],
]

export const validator = (values, user_id) => Joi.object({
  details: Joi.object({
    risk_factors: Joi.object({
      age: Joi.boolean(),
      underlying_medical_condition: Joi.boolean(),
      smoker: Joi.boolean(),
      healthcare_worker: Joi.boolean(),
    }).required(),
  })
    .unknown(true)
    .required(),
}).unknown(true)

export default function RiskFactors({ changeStep, values, onChange }) {
  const {
    session: { user_id },
  } = useSession()
  const nationalId = get(values, 'details.on_behalf_of.national_id', user_id)
  const { age } = get(values, 'details.risk_factors', {})
  const ageLimitReached =
    differenceInYears(new Date(), new Date(getBirthDate(nationalId))) >= 65

  useEffect(() => {
    if (ageLimitReached && !age) {
      onChange('details.risk_factors.age', true)
    } else if (!ageLimitReached && age) {
      onChange('details.risk_factors.age', false)
    }
  }, [age, ageLimitReached, onChange])

  const handleChange = (value) => () => {
    const currentValue = get(values, `details.risk_factors.${value}`, false)
    const newValue = !currentValue

    onChange(`details.risk_factors.${value}`, newValue || undefined)
  }

  const setNone = () => onChange('details.risk_factors', {})

  const noRiskFactors =
    typeof get(values, 'details.risk_factors') === 'object' &&
    Object.keys(get(values, 'details.risk_factors') || {}).length === 0

  const selectedSelf = get(values, 'details.on_behalf_of', false) === null

  const onBehalfOfFirstName = get(values, 'details.on_behalf_of.first_name')

  const info = ageLimitReached
    ? `${
        selectedSelf ? 'Du' : onBehalfOfFirstName
      } er 65 år eller eldre og er derfor i en risikogruppe. Marker flere egenskaper om det gjelder ${
        selectedSelf ? 'deg' : onBehalfOfFirstName
      }, eller gå videre.`
    : `Marker det som gjelder ${
        selectedSelf ? 'deg' : onBehalfOfFirstName
      } (flere valg mulig).`

  const title = getGenderText('Er %s i en risikogruppe?', values)

  return (
    <Step
      title={title}
      onSubmit={changeStep}
      submit="Gå videre"
      submitDisabled={!get(values, 'details.risk_factors')}
      info={info}
    >
      {RISKS.filter(([value]) => value !== 'age' || ageLimitReached).map(
        ([value, label]) => (
          <Button
            key={value}
            inline
            small
            onClick={handleChange(value)}
            disabled={ageLimitReached && value === 'age'}
            selected={get(values, `details.risk_factors.${value}`, false)}
          >
            {label}
          </Button>
        ),
      )}
      {!ageLimitReached && (
        <Button inline small onClick={setNone} selected={noRiskFactors}>
          Ingen av delene
        </Button>
      )}
    </Step>
  )
}

RiskFactors.propTypes = {
  changeStep: T.func.isRequired,
  values: T.object.isRequired,
  onChange: T.func.isRequired,
}
